import { TrendingUp, AlertCircle, Users, FileWarning } from "lucide-react";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import AnimatedCounter from "./AnimatedCounter";
import { cn } from "@/lib/utils";

const stats = [
  {
    icon: FileWarning,
    stat: 145000,
    suffix: "+",
    label: "Monthly credit complaints",
    source: "CFPB 2024",
    description: "Complaints have risen 18x since 2017",
  },
  {
    icon: AlertCircle,
    stat: 44,
    suffix: "%",
    label: "Reports with errors",
    source: "Consumer Reports 2024",
    description: "Almost half contain mistakes",
  },
  {
    icon: TrendingUp,
    stat: 73,
    suffix: "%",
    label: "Complaint increase",
    source: "CFPB Data",
    description: "Year-over-year rise from 2022-2023",
  },
  {
    icon: Users,
    stat: 26,
    suffix: "%",
    label: "Serious errors found",
    source: "Consumer Reports Study",
    description: "Errors that could damage your score",
  },
];

const CredibilityStats = () => {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.05 });

  return (
    <section className="py-12 lg:py-16 bg-card border-y border-border/50">
      <div className="container-custom" ref={ref}>
        {/* Header */}
        <div className={cn(
          "text-center max-w-3xl mx-auto mb-8 transition-all duration-700",
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
        )}>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-4">
            The Credit Reporting System{" "}
            <span className="text-destructive">Isn't Working</span>
          </h2>
          <p className="text-muted-foreground">
            These numbers come from public consumer complaint data and consumer research. They show why accuracy and clean documentation matter.
          </p>
        </div>

        {/* Stats grid */}
        <div className={cn(
          "grid sm:grid-cols-2 lg:grid-cols-4 gap-6 stagger-children",
          isVisible && "visible"
        )}>
          {stats.map((item, index) => (
            <div
              key={index}
              className="group relative bg-background rounded-xl p-6 border border-border/50 shadow-custom-sm hover:shadow-custom-md transition-all duration-300 hover:-translate-y-1"
            >
              {/* Icon with hover effect */}
              <div className="w-12 h-12 rounded-xl bg-destructive/10 flex items-center justify-center mb-4 group-hover:bg-destructive/20 transition-colors group-hover:scale-110 duration-300">
                <item.icon className="w-6 h-6 text-destructive" />
              </div>

              {/* Animated Stat */}
              <p className="text-3xl lg:text-4xl font-bold text-foreground mb-1">
                {isVisible ? (
                  <AnimatedCounter 
                    end={item.stat} 
                    suffix={item.suffix}
                    duration={2000}
                  />
                ) : (
                  `0${item.suffix}`
                )}
              </p>
              
              {/* Label */}
              <p className="font-semibold text-foreground mb-2">{item.label}</p>
              
              {/* Description */}
              <p className="text-sm text-muted-foreground mb-3">{item.description}</p>
              
              {/* Source */}
              <span className="text-xs text-muted-foreground italic">
                Source: {item.source}
              </span>
            </div>
          ))}
        </div>

        {/* Quote callout */}
        <div className={cn(
          "mt-8 bg-muted/50 rounded-2xl p-6 border border-border/50 transition-all duration-700 delay-300",
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
        )}>
          <blockquote className="text-center">
            <p className="text-lg md:text-xl text-foreground font-medium mb-4 italic">
              "America's credit reporting oligopoly has little incentive to treat consumers fairly when their credit reports have errors."
            </p>
            <footer className="text-muted-foreground">
              — <cite className="not-italic font-semibold">Rohit Chopra</cite>, CFPB Director, 2022
            </footer>
          </blockquote>
        </div>
      </div>
    </section>
  );
};

export default CredibilityStats;
